import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './DocumentsPopup.css';
import {
  toggleDocument,
  resetToDefaults,
  selectSelectedDocuments,
  selectProjectType,
} from '../../../redux/projectTypeSlice';
import {
  selectActiveTemplateId,
  selectActiveNotlarTemplateId,
  setActiveTemplate,
  activateBackendTemplate,
  clearActiveTemplate,
} from '../../../redux/baslikTemplateSlice';
import {
  DOCUMENT_TYPES,
  DOCUMENT_LABELS,
  DOCUMENT_CATEGORIES,
  PROJECT_TYPE_LABELS,
  COMMON_DOCUMENTS,
  DEFAULT_PINNED_DOCS,
} from '../../../constants/projectTypes';
import { setProjectMetadata } from '../../../redux/projectSlice';
import API_URL from '../../../config';

// Documents popup'taki DOCUMENT_TYPES değerleri ('baslik', 'notlar', vb.) ile
// layout'taki INITIAL_DEFS isimleri ('Baslik', 'Notlar', vb.) arasında eşleştirme.
// Pin state'i layout-name bazlı saklanır (proje metadata.pinnedDocs).
const DOC_TYPE_TO_LAYOUT_NAME = {
  vaziyet_plani:    'Vaziyet Planı',
  topraklama_plani: 'Topraklama Detayları',
  topraklama_detay: 'Topraklama Detayları',
  topraklama_hesap: 'Topraklama Hesabı',
  notlar:           'Notlar',
  semboller:        'Sembol Listesi',
  baslik:           'Baslik',
  yukleme_cetveli:  'Yükleme Cetveli',
  tek_hat_semasi:   'TekHat  Şeması',
  kolon_semasi:     'Kolon Şeması',
  pano_detay:       'Pano Detay',
  gerilim_dusum:    'Gerilim Düşümü',
  enerji_odasi:     'Enerji Odasi',
  kablo_bacasi:     'Kablo Bacası',
};

const DocumentsPopup = ({ isOpen, onClose }) => {
  const popupRef = useRef(null);
  const dispatch = useDispatch();
  const selectedDocuments = useSelector(selectSelectedDocuments);
  const projectType = useSelector(selectProjectType);
  const activeBaslikTemplateId = useSelector(selectActiveTemplateId);
  const activeNotlarTemplateId = useSelector(selectActiveNotlarTemplateId);
  const projectMetadata = useSelector(state => state.project?.projectMetadata || {});
  // Sabit (pinned) docs — layout-name bazlı set. Öncelik: proje metadata, sonra default.
  const pinnedSet = React.useMemo(() => {
    const list = Array.isArray(projectMetadata.pinnedDocs)
      ? projectMetadata.pinnedDocs
      : DEFAULT_PINNED_DOCS;
    return new Set(list);
  }, [projectMetadata.pinnedDocs]);

  const togglePin = useCallback((docType) => {
    const layoutName = DOC_TYPE_TO_LAYOUT_NAME[docType];
    if (!layoutName) return;
    const next = new Set(pinnedSet);
    if (next.has(layoutName)) next.delete(layoutName);
    else next.add(layoutName);
    dispatch(setProjectMetadata({ ...projectMetadata, pinnedDocs: Array.from(next) }));
  }, [dispatch, pinnedSet, projectMetadata]);
  const isPinned = useCallback((docType) => {
    const layoutName = DOC_TYPE_TO_LAYOUT_NAME[docType];
    return layoutName ? pinnedSet.has(layoutName) : false;
  }, [pinnedSet]);

  // Backend'den çekilen kullanıcı CAD şablonları (subType'a göre ayrı listeler)
  const [baslikTemplates, setBaslikTemplates] = useState([]);
  const [notlarTemplates, setNotlarTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [activatingId, setActivatingId] = useState(null);

  // ESC key to close
  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) onClose();
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // BASLIK veya NOTLAR seçili ise backend'den cad_template listesini çek (tek istek; subType'a böl)
  const baslikSelected = selectedDocuments.includes(DOCUMENT_TYPES.BASLIK);
  const notlarSelected = selectedDocuments.includes(DOCUMENT_TYPES.NOTLAR);
  useEffect(() => {
    if (!isOpen || (!baslikSelected && !notlarSelected)) return;
    const token = localStorage.getItem('projabze_token');
    if (!token) return;
    setLoadingTemplates(true);
    setTemplateError(null);
    fetch(`${API_URL}/api/documents?type=cad_template`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => {
        const list = Array.isArray(data?.documents) ? data.documents : (Array.isArray(data) ? data : []);
        // Eski kayıtlarda subType olmayabilir → varsayılan olarak baslik kabul et
        setBaslikTemplates(list.filter(d => !d.subType || d.subType === 'baslik'));
        setNotlarTemplates(list.filter(d => d.subType === 'notlar'));
      })
      .catch(err => {
        console.error('[DocumentsPopup] CAD şablonları yüklenemedi:', err);
        setTemplateError('Şablonlar yüklenemedi');
      })
      .finally(() => setLoadingTemplates(false));
  }, [isOpen, baslikSelected, notlarSelected]);

  // Şablon seçimi: subType ile birlikte (baslik / notlar) çağrılır
  const handleTemplateChange = useCallback(async (templateId, subType) => {
    if (!templateId) {
      dispatch(clearActiveTemplate(subType));
      return;
    }
    const activeId = subType === 'notlar' ? activeNotlarTemplateId : activeBaslikTemplateId;
    if (templateId === activeId) return;
    const list = subType === 'notlar' ? notlarTemplates : baslikTemplates;
    const tmpl = list.find(t => t.id === templateId);
    if (!tmpl) return;

    setActivatingId(templateId);
    setTemplateError(null);
    try {
      const token = localStorage.getItem('projabze_token');
      const detailRes = await fetch(`${API_URL}/api/documents/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!detailRes.ok) throw new Error('Şablon meta okunamadı');
      const detail = await detailRes.json();
      const doc = detail.document || detail;
      const payload = doc.payload || {};
      const docSubType = doc.subType || payload.subType || subType || 'baslik';

      let dxfRawText = '';
      // Başlık şablonu DXF dosyasından gelir; notlar şablonu yalnızca payload kullanır
      if (doc.fileUrl) {
        const dxfRes = await fetch(`${API_URL}${doc.fileUrl}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (dxfRes.ok) dxfRawText = await dxfRes.text();
      }
      if (docSubType === 'baslik' && !dxfRawText) throw new Error('Başlık şablonu DXF dosyası yok');

      dispatch(activateBackendTemplate({
        id: doc.id,
        name: doc.title || 'Şablon',
        subType: docSubType,
        dxfRawText,
        bindings: payload.bindings || {},
        notesPayload: docSubType === 'notlar' ? { title: payload.title || 'NOTLAR:', notes: payload.notes || [] } : null,
        docId: doc.id,
      }));
    } catch (err) {
      console.error('[DocumentsPopup] Şablon aktive edilemedi:', err);
      setTemplateError(err.message || 'Aktive edilemedi');
      dispatch(setActiveTemplate(null));
    } finally {
      setActivatingId(null);
    }
  }, [dispatch, activeBaslikTemplateId, activeNotlarTemplateId, baslikTemplates, notlarTemplates]);

  if (!isOpen) return null;

  const handleToggle = (docType) => dispatch(toggleDocument(docType));
  const handleResetToDefaults = () => dispatch(resetToDefaults());
  const isSelected = (docType) => selectedDocuments.includes(docType);
  const isCommon = (docType) => COMMON_DOCUMENTS.includes(docType);

  return (
    <div className="documents-popup-overlay">
      <div className="documents-popup" ref={popupRef}>
        {/* Header */}
        <div className="documents-popup-header">
          <div className="documents-popup-title">
            <h3>Proje Dokümanları</h3>
            <span className="project-type-badge">
              {PROJECT_TYPE_LABELS[projectType]}
            </span>
          </div>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        {/* Content */}
        <div className="documents-popup-content">
          {Object.entries(DOCUMENT_CATEGORIES).map(([categoryKey, category]) => (
            <div key={categoryKey} className="document-category">
              <div className="category-header">{category.label}</div>
              <div className="document-list">
                {category.documents.map((docType) => (
                  <React.Fragment key={docType}>
                    <div
                      className={`document-item ${isSelected(docType) ? 'selected' : ''} ${isCommon(docType) ? 'common' : ''}`}
                      onClick={() => handleToggle(docType)}
                    >
                      <span className="document-checkbox">{isSelected(docType) ? '☑' : '☐'}</span>
                      <span className="document-label">{DOCUMENT_LABELS[docType]}</span>
                      {isCommon(docType) && (
                        <span className="common-badge" title="Tüm projelerde bulunur">Ortak</span>
                      )}
                      <button
                        type="button"
                        className={`document-pin-btn ${isPinned(docType) ? 'pinned' : ''}`}
                        onClick={(e) => { e.stopPropagation(); togglePin(docType); }}
                        title={isPinned(docType) ? 'Sağ şeritte sabit — kaldırmak için tıkla' : 'Sağ şeritte sabitle'}
                      >
                        {isPinned(docType) ? '📌' : '📍'}
                      </button>
                    </div>

                    {docType === DOCUMENT_TYPES.BASLIK && isSelected(docType) && (
                      <div className="document-subselect" onClick={(e) => e.stopPropagation()}>
                        <label className="document-subselect-label">Şablon</label>
                        <select
                          className="document-subselect-input"
                          value={activeBaslikTemplateId || ''}
                          onChange={(e) => handleTemplateChange(e.target.value, 'baslik')}
                          disabled={loadingTemplates || !!activatingId}
                          title="Bu projede kullanılacak başlık şablonu"
                        >
                          <option value="">Varsayılan (built-in)</option>
                          {baslikTemplates.map((t) => (
                            <option key={t.id} value={t.id}>{t.title}</option>
                          ))}
                        </select>
                        {loadingTemplates && <span className="document-subselect-hint">Yükleniyor…</span>}
                        {!loadingTemplates && activatingId && <span className="document-subselect-hint">Şablon getiriliyor…</span>}
                        {!loadingTemplates && !activatingId && templateError && (
                          <span className="document-subselect-hint" style={{ color: '#f88' }}>{templateError}</span>
                        )}
                        {!loadingTemplates && !activatingId && !templateError && baslikTemplates.length === 0 && (
                          <span className="document-subselect-hint">
                            Dokümanlar sayfasından CAD + ile başlık şablonu oluşturabilirsiniz
                          </span>
                        )}
                      </div>
                    )}

                    {docType === DOCUMENT_TYPES.NOTLAR && isSelected(docType) && (
                      <div className="document-subselect" onClick={(e) => e.stopPropagation()}>
                        <label className="document-subselect-label">Şablon</label>
                        <select
                          className="document-subselect-input"
                          value={activeNotlarTemplateId || ''}
                          onChange={(e) => handleTemplateChange(e.target.value, 'notlar')}
                          disabled={loadingTemplates || !!activatingId}
                          title="Bu projede kullanılacak notlar şablonu"
                        >
                          <option value="">Varsayılan (built-in)</option>
                          {notlarTemplates.map((t) => (
                            <option key={t.id} value={t.id}>{t.title}</option>
                          ))}
                        </select>
                        {loadingTemplates && <span className="document-subselect-hint">Yükleniyor…</span>}
                        {!loadingTemplates && !activatingId && !templateError && notlarTemplates.length === 0 && (
                          <span className="document-subselect-hint">
                            Dokümanlar sayfasından CAD + (Notlar) ile şablon oluşturabilirsiniz
                          </span>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="documents-popup-footer">
          <div className="selected-count">{selectedDocuments.length} doküman seçili</div>
          <div className="footer-actions">
            <button
              className="reset-button"
              onClick={handleResetToDefaults}
              title="Proje tipine göre varsayılanlara sıfırla"
            >
              Varsayılanlara Dön
            </button>
            <button className="confirm-button" onClick={onClose}>Tamam</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsPopup;
